import i18n from 'i18n'
import { all, takeEvery, takeLatest, takeLeading, put, call } from 'redux-saga/effects'
import { infoDB } from 'myStore'
import { getEnvParam } from 'env'
import { isEqual } from 'lodash'
import qs from 'qs'
import { history, store as reduxStore } from 'index'
import settingSyncService, { SchemaValidationError } from 'services/settingSync'
import { isPopupMode } from 'services/popup/isPopupMode'
import { CALENDAR_EXCLUDED_KEYS, TODO_EXCLUDED_KEYS } from './settingMapping'
import { toDavPath, toDavPathList } from 'libs/dav/davPath'
import { stringToHash, basicColors } from 'utils/ui'
import { getAvailableLocale } from 'localizationUtils'
import { isSameFavoriteSet, pickLegacyFavoriteMailboxIds } from './favoriteMailboxes'
import { AccountSettings, initialState } from './types'
import actions from './actions'
import {
  ChangeSettingPayload,
  SetThemePayload,
  SetLocalePayload,
  ReloadSettingsPayload,
  ReloadSettingsFinalizePayload,
  MigrateFavoriteMailboxesPayload,
  SettingsState,
  LOCAL_KEY_ROUTING,
  SYNC_KEY_ROUTING,
  settingsVersion,
  MigrationAccessor,
  isValidSettingsCategory,
} from './types'

const settingsInfo: { userName?: string } = {}

/** history.listen 해제 함수 (재등록 시 누적 방지) */
let _unlistenHistory: (() => void) | null = null

/**
 * IndexedDB에서 설정을 로드하고 기본값과 머지한다.
 * reducers.ts(초기 state)와 RELOAD_SETTINGS saga에서 공통 사용.
 */
export const loadSettingsFromDB = async (storedSettings: SettingsState): Promise<SettingsState> => {
  const settings: Record<string, unknown> = {}

  const keys = Object.keys(storedSettings) as Array<keyof SettingsState & string>
  for (const key of keys) {
    if (key === 'localSettings') continue
    const item: unknown = await infoDB.getItem(getSettingKey(key))
    if (item != null && typeof item === 'object') {
      const initialData = storedSettings[key] as Record<string, unknown>
      const merged = { ...(item as object) }
      Object.keys(initialData).forEach((subKey) => {
        if ((merged as Record<string, unknown>)[subKey] === undefined) {
          ;(merged as Record<string, unknown>)[subKey] = initialData[subKey]
        }
      })
      settings[key] = merged
    } else {
      settings[key] = item != null ? item : storedSettings[key]
    }
  }

  const storedLocal = await infoDB.getItem(getSettingKey('localSettings'))
  const dbLocal =
    storedLocal != null && typeof storedLocal === 'object'
      ? (storedLocal as Record<string, Record<string, unknown>>)
      : {}
  const defaultLocal = storedSettings.localSettings

  // ─── locale local-only 이전(backfill) ───────────────────────
  // 과거 locale 은 sync 카테고리 general 에 저장됐다. 이제 local-only(localSettings.general.locale)다.
  // settingsVersion 을 올리지 않고, 로드 때마다 멱등하게 이전한다:
  //   1) localSettings.general.locale 이 이미 있으면 그 값(이 기기에서 이전 완료) — legacy 무시.
  //   2) 없고 legacy general.locale 이 있으면 채택(기존 사용자 최초 이전).
  //   3) 둘 다 없으면 기본값.
  // 또한 settings.general 에서 locale 을 제거해 redux general 을 오염시키지 않는다
  //   → prepareForSend 가 통째로 보내는 general 에 locale 이 없어 서버 전송 차단
  //   → 서버 수신 locale 이 general 로 역유입돼도(팝업 sync) 읽지 않음.
  const generalObj = settings.general as Record<string, unknown> | undefined
  const legacyLocale =
    generalObj != null && typeof generalObj.locale === 'string' ? generalObj.locale : undefined
  if (generalObj != null) {
    delete generalObj.locale
  }
  const dbLocalGeneralLocale =
    dbLocal.general != null && typeof dbLocal.general.locale === 'string'
      ? dbLocal.general.locale
      : undefined

  settings.localSettings = {
    email: { ...defaultLocal.email, ...(dbLocal.email ?? {}) },
    contacts: { ...defaultLocal.contacts, ...(dbLocal.contacts ?? {}) },
    calendar: { ...defaultLocal.calendar, ...(dbLocal.calendar ?? {}) },
    todo: { ...defaultLocal.todo, ...(dbLocal.todo ?? {}) },
    general: {
      ...defaultLocal.general,
      locale: dbLocalGeneralLocale ?? legacyLocale ?? defaultLocal.general.locale,
    },
  }

  // 레거시 general.locale 을 DB 의 general 키에서도 즉시 영구 제거(1회 self-healing).
  // locale 값은 위에서 localSettings.general.locale 로 이미 이전됐다. 서버 업로드를 유발하지 않도록
  // CHANGE_SETTING 이 아니라 infoDB 에 직접 쓴다(redux general 은 이미 locale 이 제거된 상태).
  // legacyLocale 이 있을 때만(=DB general 에 locale 이 실제로 있었을 때만) 쓰므로 멱등하다.
  if (legacyLocale != null && generalObj != null) {
    await infoDB.setItem(getSettingKey('general'), generalObj)
  }

  return settings as SettingsState
}

export const settingsMigration = async <T>(
  target: T,
  accessor: MigrationAccessor<T>,
): Promise<void> => {
  if (target == null) return
  if (accessor.type === 'server') {
    if (Object.keys(target as object).length === 0) return // 서버 데이터가 비어있으면 처리하지 않음.
  }

  const prevVersionRet = await accessor.getValue(target, 'version')
  const prevVersion = typeof prevVersionRet === 'number' ? (prevVersionRet as number) : 0

  // v0 → v1: editorFont 개별 키를 그룹 키로 통합 (DB 전용: 서버 데이터에서는 prevVersion != null이므로 스킵)
  if (prevVersion == 0) {
    const editorFont: Record<string, unknown> = {}
    const editorFontList = [
      'editorFontFamily',
      'editorFontSizeString',
      'editorFontColorReFwd',
      'editorFontColorNew',
    ]
    for (const key of editorFontList) {
      const item = await accessor.getValue(target, key)
      if (item != null) {
        editorFont[key] = item
        await accessor.removeValue(target, key)
      }
    }
    if (Object.keys(editorFont).length > 0) {
      await accessor.setValue(target, 'editorFont', editorFont)
    }
  }

  // v1 → v2: local 필드를 localSettings 통합 키로 이동 + flat sync keys를 카테고리로
  if (prevVersion < 2) {
    if (accessor.type === 'server') {
      const calendarData = await accessor.getValue(target, 'calendar')
      // server에서 오는 데이터는 version 2 이전인 경우,
      // 1. icsSubscriptions 이외의 데이터는 모두 유효하지 않은 것으로 간주한다.
      // 그러므로 'server' type인 경우, target은 object이므로, 하위의 모든 key를 제거한다.
      Object.keys(target as object).forEach((key) => {
        delete (target as Record<string, unknown>)[key]
      })
      if (calendarData != null && typeof calendarData === 'object') {
        const calObj = calendarData as Record<string, unknown>
        // 1-1. calendar.icsSubscriptions 가  있으면 이 부분만 server setting으로 유지한다.
        if (calObj.icsSubscriptions != null) {
          await accessor.setValue(target, 'calendar', { icsSubscriptions: calObj.icsSubscriptions })
        }
      }
    } else {
      // 2. db에 존재하는 데이터는 version 1인 경우, 모두 유효한 데이터로 간주하여 처리한다.
      const localSettings: Record<string, Record<string, unknown>> = {
        account: {},
        email: {},
        contacts: {},
        calendar: {},
        todo: {},
        // locale 이 LOCAL_KEY_ROUTING 으로 이 그룹에 라우팅되므로 초기화 필요(아래 flat key 루프).
        general: {},
      }

      // 1. calendar에서 local 필드 추출
      const calendarData = await accessor.getValue(target, 'calendar')
      if (calendarData != null && typeof calendarData === 'object') {
        const calObj = calendarData as Record<string, unknown>
        let changed = false
        for (const key of CALENDAR_EXCLUDED_KEYS) {
          if (calObj[key] !== undefined) {
            localSettings.calendar[key] = calObj[key]
            delete calObj[key]
            changed = true
          }
        }
        if (changed) {
          await accessor.setValue(target, 'calendar', calObj)
        }
      }

      // 2. todo에서 local 필드 추출
      const todoData = await accessor.getValue(target, 'todo')
      if (todoData != null && typeof todoData === 'object') {
        const todoObj = todoData as Record<string, unknown>
        let changed = false
        for (const key of TODO_EXCLUDED_KEYS) {
          if (todoObj[key] !== undefined) {
            localSettings.todo[key] = todoObj[key]
            delete todoObj[key]
            changed = true
          }
        }
        if (changed) {
          await accessor.setValue(target, 'todo', todoObj)
        }
      }

      // 3. flat local keys → localSettings.email / localSettings.contacts
      for (const [flatKey, group] of Object.entries(LOCAL_KEY_ROUTING)) {
        const item = await accessor.getValue(target, flatKey)
        if (item != null) {
          localSettings[group][flatKey] = item
          await accessor.removeValue(target, flatKey)
        }
      }

      // 5. legacy 키 → email.viewMode (sync 카테고리로 직접 이전, step 8에서 주입)
      //    - flat 'mailLayout' 우선 (ca0de8c85 이후 v0 사용자)
      //    - 'emailListOnly' fallback (ca0de8c85 이전 v0 사용자)
      //    - 두 키 모두 존재 시 mailLayout이 viewMode 값으로 채택되지만, emailListOnly도 함께 정리
      let viewModeFromList: string | null = null

      const flatMailLayout = await accessor.getValue(target, 'mailLayout')
      if (flatMailLayout != null && typeof flatMailLayout === 'string') {
        viewModeFromList = flatMailLayout
        await accessor.removeValue(target, 'mailLayout')
      }

      const emailListOnly = await accessor.getValue(target, 'emailListOnly')
      if (emailListOnly != null) {
        if (viewModeFromList == null) {
          viewModeFromList = emailListOnly ? 'noSplit' : 'splitHorizontal'
        }
        await accessor.removeValue(target, 'emailListOnly')
      }

      // 6. 기존 localSettings가 있으면 머지
      const existingLocal = await accessor.getValue(target, 'localSettings')
      if (existingLocal != null && typeof existingLocal === 'object') {
        const existing = existingLocal as Record<string, Record<string, unknown>>
        for (const group of Object.keys(localSettings)) {
          if (existing[group] != null) {
            localSettings[group] = { ...existing[group], ...localSettings[group] }
          }
        }
      }

      // 7. localSettings 통합 키 저장
      await accessor.setValue(target, 'localSettings', localSettings)

      // 8. flat sync keys → 카테고리 그룹 키로 마이그레이션
      const syncCategories: Record<string, Record<string, unknown>> = {
        account: {},
        general: {},
        email: {},
      }

      for (const [flatKey, category] of Object.entries(SYNC_KEY_ROUTING)) {
        const item = await accessor.getValue(target, flatKey)
        if (item != null) {
          syncCategories[category][flatKey] = item
          await accessor.removeValue(target, flatKey)
        }
      }
      // 기존 카테고리 그룹 키가 있으면 머지
      for (const category of Object.keys(syncCategories)) {
        const existing = await accessor.getValue(target, category)
        if (existing != null && typeof existing === 'object') {
          syncCategories[category] = {
            ...(existing as Record<string, unknown>),
            ...syncCategories[category],
          }
        }
        // viewMode 특별 처리: email 카테고리에 viewMode가 없을 때만 viewModeFromList 주입
        // (서버에서 받은 viewMode가 마이그레이션 fallback에 덮이지 않도록 보장)
        if (
          category === 'email' &&
          viewModeFromList != null &&
          syncCategories.email.viewMode == null
        ) {
          syncCategories.email.viewMode = viewModeFromList
        }
        if (Object.keys(syncCategories[category]).length > 0) {
          await accessor.setValue(target, category, syncCategories[category])
        }
      }
    }
  }

  // v2 → v3: 서명 키를 localSettings.account → account 로 이전.
  // 헬퍼는 존재검사 기반 멱등이며, server 데이터에는 localSettings가 없어 no-op이다.
  // version 저장은 최신 블록(현재 v3→v4) 말미로 이전되어 전체 마이그레이션 통과 후 1회만 수행된다.
  // 전환기 버전 핑퐁(구버전 클라이언트가 서버 version을 2로 되돌림)으로 이 블록이 반복 실행될 수
  // 있으므로, 비멱등 로직을 추가하면 안 된다.
  if (prevVersion < 3) {
    await migrateSignatureKeys(target, accessor)
  }

  // v3 → v4: CalDAV/CardDAV 동기화 폴더 식별자(calendarSyncTargets/defaultCalendar,
  // todoSyncTargets/defaultFolder)를 host 포함 절대 URL → host 제외 path 로 정규화한다.
  // 권고 서버주소(host)가 바뀌어도 클라이언트 간 동기화 플래그가 깨지지 않도록 하기 위함이다.
  // toDavPath/toDavPathList 자체가 멱등이므로 이미 path 인 값을 재실행해도 값이 바뀌지 않는다.
  if (prevVersion < 4) {
    await migrateDavPaths(target, accessor)
    await accessor.setValue(target, 'version', settingsVersion)
  }
}

/**
 * DAV path 정규화 대상 필드 매핑 (settingsMigration v3→v4, RELOAD_SETTINGS 변경 감지에서 공용).
 * icsSubscriptions(외부 ICS 구독 URL)는 host 가 본질적인 정보이므로 대상에서 제외한다.
 */
export const DAV_PATH_FIELDS: Record<'calendar' | 'todo', { list: string; default: string }> = {
  calendar: { list: 'calendarSyncTargets', default: 'defaultCalendar' },
  todo: { list: 'todoSyncTargets', default: 'defaultFolder' },
}

/**
 * DAV_PATH_FIELDS에 정의된 필드만 뽑아낸 얕은 객체를 반환한다.
 * RELOAD_SETTINGS에서 마이그레이션 전/후 값을 비교해 서버 재업로드 여부를 판단하는 데 사용한다.
 */
export const pickDavPathFields = (
  data: unknown,
  fields: { list: string; default: string },
): Record<string, unknown> => {
  if (data == null || typeof data !== 'object') return {}
  const source = data as Record<string, unknown>
  return { [fields.list]: source[fields.list], [fields.default]: source[fields.default] }
}

/**
 * account에서 서명 키(signatureForNew/signatureForReplyForward)만 뽑아낸 얕은 객체를 반환한다.
 * v2→v3 서명 마이그레이션 전/후 값을 비교해 서버 재업로드 여부를 판단하는 데 사용한다.
 */
export const pickSignatureFields = (data: unknown): Record<string, unknown> => {
  if (data == null || typeof data !== 'object') return {}
  const source = data as Record<string, unknown>
  return {
    signatureForNew: source.signatureForNew,
    signatureForReplyForward: source.signatureForReplyForward,
  }
}

const migrateDavPaths = async <T>(target: T, accessor: MigrationAccessor<T>): Promise<void> => {
  for (const category of Object.keys(DAV_PATH_FIELDS) as Array<keyof typeof DAV_PATH_FIELDS>) {
    const fields = DAV_PATH_FIELDS[category]
    const data = await accessor.getValue(target, category)
    if (data == null || typeof data !== 'object') continue

    const obj = data as Record<string, unknown>
    const next: Record<string, unknown> = { ...obj }
    let changed = false

    const listValue = obj[fields.list]
    if (Array.isArray(listValue)) {
      const converted = toDavPathList(listValue as string[])
      if (!isEqual(converted, listValue)) {
        next[fields.list] = converted
        changed = true
      }
    }

    // defaultValue가 null이면 "미초기화" 의미이므로 그대로 유지한다.
    const defaultValue = obj[fields.default]
    if (typeof defaultValue === 'string') {
      const converted = toDavPath(defaultValue)
      if (converted !== defaultValue) {
        next[fields.default] = converted
        changed = true
      }
    }

    if (changed) {
      await accessor.setValue(target, category, next)
    }
  }
}

const SIGNATURE_KEYS = ['signatureForNew', 'signatureForReplyForward'] as const

const migrateSignatureKeys = async <T>(
  target: T,
  accessor: MigrationAccessor<T>,
): Promise<void> => {
  const localSettings = await accessor.getValue(target, 'localSettings')
  if (localSettings == null || typeof localSettings !== 'object') return
  const local = localSettings as Record<string, Record<string, unknown>>
  const localAccount = local.account
  if (localAccount == null || typeof localAccount !== 'object') return

  const accountRet = await accessor.getValue(target, 'account')
  const account =
    accountRet != null && typeof accountRet === 'object'
      ? { ...(accountRet as Record<string, unknown>) }
      : {}

  let changed = false
  for (const key of SIGNATURE_KEYS) {
    if (localAccount[key] !== undefined) {
      account[key] = localAccount[key]
      delete localAccount[key]
      changed = true
    }
  }
  if (!changed) return

  await accessor.setValue(target, 'account', account)
  await accessor.setValue(target, 'localSettings', local)
}

export function getSettingKey(key: string): string {
  if (settingsInfo.userName == null) {
    const email = getEnvParam('userEmail')
    if (email != null) {
      let userName = email
      if (__ENV_PRODUCTION__) {
        userName = stringToHash(email)
      }
      settingsInfo.userName = userName
    } else {
      return `app.settings.${key}`
    }
  }
  return `${settingsInfo.userName}-settings.${key}`
}

/**
 * settings 변경을 처리하는 saga.
 *
 * payload는 Record<string, unknown> 맵으로, 키는 다음 두 형태를 지원한다:
 *   - dot-path: 'general.locale', 'localSettings.email.theme' 등
 *   - 카테고리 전체: 'calendar', 'ai' 등 (value가 카테고리 전체 객체)
 *
 * 동작 원리:
 *   1. payload의 각 엔트리를 순회하며 카테고리별 버퍼(updatedCategories)에 누적한다.
 *   2. 각 엔트리의 key/value로 트리 객체를 생성한 뒤, Redux 기존 데이터(또는 이전 루프 결과)와
 *      deepMerge하여 updatedCategories에 저장한다.
 *   3. dot-path 키든 카테고리 전체 교체든, 항상 기존 데이터 위에 merge되므로
 *      나머지 키가 보존된다.
 *   4. 모든 엔트리 처리 후, 변경된 카테고리만 DB에 저장하고 단일 SET_STATE로 dispatch한다.
 */
export function* CHANGE_SETTING(action: {
  type?: string
  payload: ChangeSettingPayload
  onComplete?: () => void
}) {
  const entries = action.payload
  const settings = (reduxStore.getState() as { settings: SettingsState }).settings

  // 카테고리별 버퍼: 기존 데이터 위에 변경사항을 누적
  const updatedCategories: Record<string, unknown> = {}

  // merge가 아니라 replace로 동작해야 하는 key는 버퍼에 미리 준비한다 (Redux state 직접 변경 방지).
  const accountSettings = entries?.account as AccountSettings
  if (accountSettings != null) {
    const accountBase = { ...settings.account } as Record<string, unknown>
    if (accountSettings.alwaysCc != null) {
      accountBase.alwaysCc = {}
    }
    if (accountSettings.alwaysBcc != null) {
      accountBase.alwaysBcc = {}
    }
    updatedCategories.account = accountBase
  }

  const deepMerge = (
    target: Record<string, unknown>,
    source: Record<string, unknown>,
  ): Record<string, unknown> => {
    const result = { ...target }
    for (const [k, srcVal] of Object.entries(source)) {
      const tgtVal = result[k]
      if (
        tgtVal != null &&
        srcVal != null &&
        typeof tgtVal === 'object' &&
        typeof srcVal === 'object' &&
        !Array.isArray(tgtVal) &&
        !Array.isArray(srcVal)
      ) {
        result[k] = deepMerge(tgtVal as Record<string, unknown>, srcVal as Record<string, unknown>)
      } else {
        result[k] = srcVal
      }
    }
    return result
  }

  for (const [key, value] of Object.entries(entries)) {
    const segments = key.split('.')
    const category = segments[0]

    // SettingsState에 명시적으로 선언된 카테고리만 처리 (인덱스 시그니처로 인한 임의 키 유입 차단)
    if (!isValidSettingsCategory(category)) {
      console.warn(
        `[CHANGE_SETTING] Ignored unknown settings category: "${category}" (from key "${key}")`,
      )
      continue
    }

    // 버퍼에 없으면 Redux 현재 값으로 초기화
    if (updatedCategories[category] == null) {
      const current: unknown = settings[category]
      updatedCategories[category] =
        current != null && typeof current === 'object'
          ? { ...(current as Record<string, unknown>) }
          : current
    }

    // key/value로 트리 객체를 생성
    const restSegments = segments.slice(1)
    let incoming: unknown
    if (restSegments.length === 0) {
      // 카테고리 전체 값
      incoming = value
    } else {
      // dot-path → 트리 변환
      incoming = {}
      let node = incoming as Record<string, unknown>
      for (let i = 0; i < restSegments.length - 1; i++) {
        node[restSegments[i]] = {}
        node = node[restSegments[i]] as Record<string, unknown>
      }
      node[restSegments[restSegments.length - 1]] = value
    }

    // 기존 데이터와 deepMerge
    const existing = updatedCategories[category]
    if (
      existing != null &&
      incoming != null &&
      typeof existing === 'object' &&
      typeof incoming === 'object' &&
      !Array.isArray(existing) &&
      !Array.isArray(incoming)
    ) {
      updatedCategories[category] = deepMerge(
        existing as Record<string, unknown>,
        incoming as Record<string, unknown>,
      )
    } else {
      updatedCategories[category] = incoming
    }
  }

  // 변경된 카테고리만 DB에 저장하고, 단일 SET_STATE로 Redux에 반영
  const statePayload: Record<string, unknown> = {}

  for (const [category, data] of Object.entries(updatedCategories)) {
    const saveCategory = async (): Promise<void> => {
      // popup(EmailEditorWindow)은 부모와 공유하는 infoDB 를 오염시키지 않도록 persist 를 스킵한다.
      // SET_STATE(아래)는 그대로 두어 Redux 의 defaultSendAsAccount 등 send 경로 의존 값은 보존한다.
      if (isPopupMode()) return
      // 나중에 좀더 자세히 손보자.
      if (category === 'version') {
        await infoDB.setItem(getSettingKey(category), settingsVersion)
      } else {
        await infoDB.setItem(getSettingKey(category), data)
      }
    }
    if (isEqual(settings[category], data) !== true) {
      yield saveCategory()
      statePayload[category] = data
    }
  }

  if (Object.keys(statePayload).length > 0) {
    yield put({
      type: 'settings/SET_STATE',
      payload: statePayload,
    })
  }

  if (action.onComplete != null) {
    action.onComplete()
  }
}

export function* SET_THEME(action: { type: string; payload: SetThemePayload }) {
  const {
    payload: { theme },
  } = action
  const nextTheme = theme === 'dark' ? 'dark' : 'default'
  yield document.querySelector('html')?.setAttribute('data-kit-theme', nextTheme)
  yield put({
    type: 'settings/CHANGE_SETTING',
    payload: { 'localSettings.email.theme': nextTheme },
  })
}

export function* SET_LOCALE(action: {
  type: string
  payload: SetLocalePayload
  onComplete?: () => void
}) {
  const {
    payload: { locale },
  } = action
  const availableLocale = getAvailableLocale(locale)
  // locale 은 local-only — localSettings.general.locale 에 저장(서버로 전송하지 않음).
  yield CHANGE_SETTING({
    payload: { 'localSettings.general.locale': availableLocale },
  })

  const t = yield call(i18n.changeLanguage, availableLocale)
  if (t != null) {
    yield put({ type: 'menu/RELOAD' })
  }

  if (action.onComplete != null) {
    action.onComplete()
  }
}

export function* SETUP() {
  const changeSettings = (search: string): void => {
    const query = qs.parse(search, { ignoreQueryPrefix: true })
    Object.keys(query).forEach((key) => {
      let value: string | boolean | null | undefined
      switch (query[key]) {
        case 'false':
          value = false
          break
        case 'true':
          value = true
          break
        default:
          value = query[key] as string | null | undefined
          break
      }
      if (key === 'theme') {
        reduxStore.dispatch({ type: 'settings/SET_THEME', payload: { theme: value } })
        return
      }
      if (key === 'locale' || key === 'lang') {
        reduxStore.dispatch({ type: 'settings/SET_LOCALE', payload: { locale: value } })
        return
      }
    })
  }
  // 재등록 전 이전 리스너 해제 (RELOAD_SETTINGS → SETUP 재호출 시 누적 방지)
  if (_unlistenHistory) {
    _unlistenHistory()
    _unlistenHistory = null
  }

  yield changeSettings(history.location.search)
  _unlistenHistory = (yield history.listen((sparams: Location) => {
    const { search } = sparams
    changeSettings(search)
  })) as () => void

  const initTheme = async (): Promise<void> => {
    const { search } = history.location
    const query = qs.parse(search, { ignoreQueryPrefix: true })
    // localSettings 통합 키에서 theme 로드 (마이그레이션 후 개별 키 삭제됨)
    const storedLocal = (await infoDB.getItem(getSettingKey('localSettings'))) as Record<
      string,
      Record<string, unknown>
    > | null
    const theme = query.theme || storedLocal?.email?.theme || 'default'
    reduxStore.dispatch({ type: 'settings/SET_THEME', payload: { theme } })
  }
  yield initTheme()

  const initLocale = async (): Promise<void> => {
    // locale 은 local-only — localSettings.general.locale 에서 로드한다.
    // 기존 사용자(legacy)는 아직 localSettings 에 없고 general.locale 에 남아 있을 수 있어 fallback 한다
    // (loadSettingsFromDB backfill 과 동일 우선순위). dispatch 로 localSettings.general.locale 에 확정 저장돼
    // backfill 이 DB 에 영속된다(멱등).
    const storedLocal = (await infoDB.getItem(getSettingKey('localSettings'))) as Record<
      string,
      Record<string, unknown>
    > | null
    const storedGeneral = (await infoDB.getItem(getSettingKey('general'))) as Record<
      string,
      unknown
    > | null
    const locale = storedLocal?.general?.locale ?? storedGeneral?.locale
    if (locale) {
      reduxStore.dispatch({
        type: 'settings/CHANGE_SETTING',
        payload: { 'localSettings.general.locale': locale },
      })
    }
  }
  yield initLocale()
}

export function* RELOAD_SETTINGS(action: { type: string; payload: ReloadSettingsPayload }) {
  const {
    payload: { name, email },
  } = action
  delete settingsInfo.userName

  const dbAccessor: MigrationAccessor<typeof infoDB> = {
    type: 'db',
    getValue: (target, key) => target.getItem(getSettingKey(key)),
    setValue: (target, key, value) => target.setItem(getSettingKey(key), value),
    removeValue: (target, key) => target.removeItem(getSettingKey(key)),
  }

  // settingsMigration(v3→v4 DAV path 정규화)이 실제로 calendar/todo 값을 바꿨는지 추적한다.
  // 값이 바뀐 경우에만 UPLOAD_SETTINGS를 dispatch해 서버에도 반영한다(불필요한 업로드 방지).
  const davMigratedCategories = { calendar: false, todo: false }
  // OMD-225 Phase 4: v2→v3 서명 키 마이그레이션(localSettings.account → account)이 실제 값을
  // 옮겼는지 추적한다. 옮겼다면 loaded 이후 account를 1회 업로드해 서버에 반영한다(과거 업로드 누락 보완).
  let signatureMigrated = false

  const loadWithMigration = async (storedSettings: SettingsState): Promise<SettingsState> => {
    const beforeCalendar = pickDavPathFields(
      await dbAccessor.getValue(infoDB, 'calendar'),
      DAV_PATH_FIELDS.calendar,
    )
    const beforeTodo = pickDavPathFields(
      await dbAccessor.getValue(infoDB, 'todo'),
      DAV_PATH_FIELDS.todo,
    )
    const beforeAccountSig = pickSignatureFields(await dbAccessor.getValue(infoDB, 'account'))

    await settingsMigration(infoDB, dbAccessor)

    const afterCalendar = pickDavPathFields(
      await dbAccessor.getValue(infoDB, 'calendar'),
      DAV_PATH_FIELDS.calendar,
    )
    const afterTodo = pickDavPathFields(
      await dbAccessor.getValue(infoDB, 'todo'),
      DAV_PATH_FIELDS.todo,
    )
    const afterAccountSig = pickSignatureFields(await dbAccessor.getValue(infoDB, 'account'))
    davMigratedCategories.calendar = !isEqual(beforeCalendar, afterCalendar)
    davMigratedCategories.todo = !isEqual(beforeTodo, afterTodo)
    signatureMigrated = !isEqual(beforeAccountSig, afterAccountSig)

    const loadedSettings = await loadSettingsFromDB(storedSettings)
    if (loadedSettings.version !== settingsVersion) {
      // 마이그레이션이 정상 수행되었다면 도달하지 않아야 하는 경로.
      // DB write 실패나 향후 settingsVersion 증가 시점의 edge case를 감지하기 위한 방어적 경고.
      console.warn(
        `Settings version is not matched. Migrated from version ${loadedSettings.version} to ${settingsVersion}.`,
      )
    }
    return loadedSettings
  }
  const newState: SettingsState = yield call(loadWithMigration, initialState)

  if (name == null && email == null) {
    _resetUploadRetry()

    // locale 은 local-only 지만 "디바이스 선호 언어"라 로그아웃 후에도 유지한다
    // (다음 로그인 화면이 마지막 언어로 표시). localSettings 키는 아래에서 통째로 삭제되므로
    // locale 만 추려 재영속하고 logoutState 에도 포함한다.
    const preservedLocale =
      newState.localSettings?.general?.locale ?? initialState.localSettings.general.locale

    yield call([infoDB, infoDB.removeItem], getSettingKey('localSettings'))
    yield call([infoDB, infoDB.removeItem], getSettingKey('account'))
    yield call([infoDB, infoDB.removeItem], getSettingKey('email'))
    yield call([infoDB, infoDB.removeItem], getSettingKey('calendar'))
    yield call([infoDB, infoDB.removeItem], getSettingKey('todo'))
    yield call([infoDB, infoDB.removeItem], getSettingKey('ai'))

    // locale 만 다시 영속(다음 세션 로드 시 loadSettingsFromDB 가 나머지 그룹은 기본값으로 채운다).
    yield call([infoDB, infoDB.setItem], getSettingKey('localSettings'), {
      general: { locale: preservedLocale },
    })

    const logoutState: Partial<SettingsState> = {}
    if (newState.general != null) {
      logoutState.general = newState.general
    }
    // SET_STATE 는 top-level shallow merge 라 localSettings 를 넣으면 통째로 교체된다.
    // 기본값 전체 + 보존한 locale 로 구성해 다른 그룹 참조가 undefined 가 되지 않게 한다.
    logoutState.localSettings = {
      ...initialState.localSettings,
      general: { locale: preservedLocale },
    }
    if (newState.version != null) {
      logoutState.version = newState.version
    }
    yield put({
      type: 'settings/SET_STATE',
      payload: { ...logoutState },
    })
  } else {
    yield put({
      type: 'settings/SET_STATE',
      payload: { ...newState },
    })
    // OMD-225 Phase 1: DAV 마이그레이션 업로드와 accountName/defaultSendAsAccount 조건부 쓰기는
    // 서버 로드(loaded) 이후 서버값이 반영된 상태에서 재평가해야 하므로 여기서 하지 않고
    // RELOAD_SETTINGS_FINALIZE(landing이 sync() 후 dispatch)로 미룬다.
  }

  yield SETUP()

  const applyLocale = async (locale: string): Promise<void> => {
    const availableLocale = getAvailableLocale(locale)
    i18n.changeLanguage(availableLocale)
  }

  // locale 은 local-only — localSettings.general.locale 에서 읽는다.
  yield call(
    applyLocale,
    newState.localSettings?.general?.locale ?? initialState.localSettings.general.locale,
  )

  // Phase-1 완료 통지 → landing이 이 시점에 sync()를 호출한다(로컬 로드가 서버 로드보다 먼저 완료됨을 보장).
  if (action.payload.onComplete != null) {
    action.payload.onComplete({ davMigratedCategories, signatureMigrated, name, email })
  }
}

/**
 * OMD-225 Phase 1 (Phase-2 마무리): RELOAD_SETTINGS 의 조건부 쓰기/DAV 업로드를
 * 서버 로드(loaded) 이후 "현재 Redux(서버값 반영)" 기준으로 재평가해 수행한다.
 * landing 이 sync() 완료 후 dispatch 한다.
 */
export function* RELOAD_SETTINGS_FINALIZE(action: { payload: ReloadSettingsFinalizePayload }) {
  const { davMigratedCategories, signatureMigrated, name, email } = action.payload

  // 서버값이 반영된 현재 Redux 상태를 기준으로 판단한다(로컬-only stale 값이 아님).
  const current = (reduxStore.getState() as { settings: SettingsState }).settings

  if (email != null) {
    // 서버에 accountName 이 이미 있으면 여기서 기본값으로 덮어쓰지 않는다(초기화 방지의 핵심).
    if (!current.account?.accountName) {
      let displayName = name
      if (!displayName || displayName.length === 0) {
        displayName = email.split('@')[0]
      }

      yield CHANGE_SETTING({
        payload: {
          'account.accountName': displayName,
          'account.accountSenderName': displayName,
          'account.accountColor': basicColors[Math.abs(stringToHash(email)) % basicColors.length],
        },
      })
    }

    if (!current.localSettings?.email?.defaultSendAsAccount) {
      yield CHANGE_SETTING({
        payload: { 'localSettings.email.defaultSendAsAccount': { name: null, email: email } },
      })
    }
  }

  // DAV 마이그레이션(calendar/todo)과 서명 마이그레이션(account)으로 값이 바뀐 경우에만 서버 업로드한다.
  // 게이트가 loaded 확인 후 통과하며, update()는 전체 설정을 PUT하므로 카테고리 플래그는 "변경 있었음" 표식이다.
  if (davMigratedCategories.calendar || davMigratedCategories.todo || signatureMigrated) {
    yield put({
      type: actions.UPLOAD_SETTINGS,
      payload: {
        ...(davMigratedCategories.calendar ? { calendar: true } : {}),
        ...(davMigratedCategories.todo ? { todo: true } : {}),
        ...(signatureMigrated ? { account: true } : {}),
      },
    })
  }
}

/**
 * 구버전 로컬(IndexedDB) 즐겨찾기 목록을 서버 settings(email.favoriteMailboxes)로 1회 이전한다.
 * DataManager 가 메일함 목록 로드 후(기본값 계산 가능 시점) dispatch 한다.
 * 구버전은 사용자가 손대지 않아도 role 기본값을 isInitialized:true 로 자동 저장했으므로,
 * 기본값과 같은 집합이면 미커스터마이즈로 보고 업로드하지 않는다
 * (기본값만 가진 기기가 먼저 로그인해 커스텀 기기의 값을 덮어쓰는 유실 방지).
 * legacy 는 mailbox id 목록이므로 payload.toEntries 로 canonical 항목(경로/virtual:<id>)으로 변환해
 * 비교·저장한다. 현재 메일함으로 해석되지 않는 id(삭제된 폴더)는 변환 단계에서 버린다.
 *   - 서버 로드 loaded 아님              → 아무것도 하지 않음(로컬 보존), onComplete(false) 로 재시도 유도
 *   - 서버 값 있음                        → 서버 우선, 로컬 키만 삭제
 *   - 서버 값 없음 + legacy = 기본값 집합   → 업로드 없이 로컬 키 삭제
 *   - 서버 값 없음 + legacy ≠ 기본값       → 저장·업로드 후 로컬 키 삭제
 *   - legacy 없음/미초기화                → 로컬 키 삭제(있으면)만
 */
export function* MIGRATE_FAVORITE_MAILBOXES(action: { payload: MigrateFavoriteMailboxesPayload }) {
  const { localKey, defaultEntries, toEntries, onComplete } = action.payload
  if (isPopupMode() || settingSyncService.getServerLoadState() !== 'loaded') {
    onComplete?.(false)
    return
  }

  try {
    const legacy: unknown = yield call([infoDB, infoDB.getItem], localKey)
    if (legacy == null) {
      onComplete?.(true)
      return
    }

    const current = (reduxStore.getState() as { settings: SettingsState }).settings
    const legacyIds = pickLegacyFavoriteMailboxIds(legacy)
    const legacyEntries = legacyIds != null ? toEntries(legacyIds) : null
    if (
      current.email?.favoriteMailboxes === undefined &&
      legacyEntries != null &&
      !isSameFavoriteSet(legacyEntries, defaultEntries)
    ) {
      yield call(CHANGE_SETTING, { payload: { 'email.favoriteMailboxes': legacyEntries } })
      yield put({ type: actions.UPLOAD_SETTINGS, payload: { email: true } })
    }

    yield call([infoDB, infoDB.removeItem], localKey)
    onComplete?.(true)
  } catch (e) {
    console.error('Error occurred while migrating favorite mailboxes:', e)
    onComplete?.(false)
  }
}

const UPLOAD_RETRY_MAX = 5
const UPLOAD_RETRY_DELAY_BLOCKED = 1000
const UPLOAD_RETRY_DELAY_FAILED = 2000

type UploadRetryType = 'blocked' | 'network'

let _uploadRetryTimer: ReturnType<typeof setTimeout> | null = null
let _blockedRetryCount = 0
let _failedRetryCount = 0

const _clearUploadRetryTimer = (): void => {
  if (_uploadRetryTimer != null) {
    clearTimeout(_uploadRetryTimer)
    _uploadRetryTimer = null
  }
}

// OMD-225 Phase 3-b: 저장(save/PUT) 재시도 소진 실패를 앱 계층에 알리기 위한 핸들러 훅.
// packages(store)는 apps(UI)를 import할 수 없으므로, 앱이 알림 함수를 주입한다(의존성 역전).
type SettingsSaveFailureHandlers = {
  onFailed: (retry: () => void) => void
  onRecovered: () => void
  // 스키마 검증 실패(비재시도, 데이터 malformed) 시 호출. 재시도가 무의미하므로 재로딩을 유도한다.
  onSchemaError: () => void
}
let _saveFailureHandlers: SettingsSaveFailureHandlers | null = null
let _saveFailureActive = false

export const setSettingsSaveFailureHandlers = (
  handlers: SettingsSaveFailureHandlers | null,
): void => {
  _saveFailureHandlers = handlers
}

export const _resetUploadRetry = (): void => {
  _clearUploadRetryTimer()
  _blockedRetryCount = 0
  _failedRetryCount = 0
  _saveFailureActive = false
}

const _scheduleUploadRetry = (delayMs: number, retryType: UploadRetryType): void => {
  _clearUploadRetryTimer()
  _uploadRetryTimer = setTimeout(() => {
    _uploadRetryTimer = null
    reduxStore.dispatch({ type: actions.UPLOAD_SETTINGS, payload: {}, retry: retryType })
  }, delayMs)
}

const _isRetryableError = (error: unknown): boolean => {
  if (error instanceof SchemaValidationError) {
    return false
  }
  return true
}

export function* UPLOAD_SETTINGS(action: {
  payload: Record<string, unknown>
  retry?: UploadRetryType
}) {
  // popup(EmailEditorWindow)은 부모와 동시에 서버로 설정을 push 하지 않는다.
  // settingSyncService.update() 는 setSettingsProvider 미설정 시 SchemaValidationError
  // throw + window.alert 를 유발하므로, saga 진입부에서 early return 으로 차단한다.
  if (isPopupMode()) return

  // 서버에서 수신한 설정을 로컬에 적용하는 중이면 일정 시간 후 재시도한다.
  if (settingSyncService.isApplyingServerSettings()) {
    if (action.retry === 'blocked') {
      _blockedRetryCount++
      if (_blockedRetryCount < UPLOAD_RETRY_MAX) {
        _scheduleUploadRetry(UPLOAD_RETRY_DELAY_BLOCKED, 'blocked')
      }
    } else {
      _blockedRetryCount = 0
      _scheduleUploadRetry(UPLOAD_RETRY_DELAY_BLOCKED, 'blocked')
    }
    return
  }

  // update 진입: 대기 중인 재시도 타이머를 해제하고 blocked 카운터를 초기화한다.
  _clearUploadRetryTimer()
  _blockedRetryCount = 0

  const entries = action.payload
  const settings = (reduxStore.getState() as { settings: SettingsState }).settings

  const targetCategories: Record<string, unknown> = {}

  for (const key of Object.keys(entries)) {
    targetCategories[key] = settings[key as keyof SettingsState]
  }

  try {
    yield call(settingSyncService.update, targetCategories)
    _failedRetryCount = 0
    // 저장 성공 → 이전에 띄운 저장 실패 배너가 있으면 닫는다. (OMD-225 Phase 3-b)
    if (_saveFailureActive) {
      _saveFailureActive = false
      _saveFailureHandlers?.onRecovered()
    }
  } catch (e) {
    console.error('Error occurred while updating server settings:', e)
    if (_isRetryableError(e)) {
      if (action.retry === 'network') {
        _failedRetryCount++
      }
      if (_failedRetryCount < UPLOAD_RETRY_MAX) {
        _scheduleUploadRetry(UPLOAD_RETRY_DELAY_FAILED, 'network')
      } else {
        // 재시도 소진 → 조용히 포기하지 않고 사용자에게 고지 + 재시도 액션 제공. (OMD-225 Phase 3-b)
        // update()는 payload와 무관하게 전체 설정을 PUT하므로, 재시도는 빈 payload로 현재 상태를 재전송한다.
        _saveFailureActive = true
        _saveFailureHandlers?.onFailed(() => {
          _failedRetryCount = 0
          reduxStore.dispatch({ type: actions.UPLOAD_SETTINGS, payload: {} })
        })
      }
    } else {
      // 비재시도 오류(SchemaValidationError, 데이터 malformed) → 재시도 무의미 → 재로딩 배너로 고지.
      // (기존 settingSync 의 crude window.alert 를 앱 계층 배너로 통일 — OMD-225)
      _saveFailureHandlers?.onSchemaError()
    }
  }
}

export default function* rootSaga() {
  yield all([
    takeEvery(actions.CHANGE_SETTING, CHANGE_SETTING as any),
    takeEvery(actions.SET_THEME, SET_THEME as any),
    takeEvery(actions.SET_LOCALE, SET_LOCALE as any),
    takeEvery(actions.UPLOAD_SETTINGS, UPLOAD_SETTINGS as any),
    takeLatest(actions.RELOAD_SETTINGS, RELOAD_SETTINGS as any),
    takeLatest(actions.RELOAD_SETTINGS_FINALIZE, RELOAD_SETTINGS_FINALIZE as any),
    takeLeading(actions.MIGRATE_FAVORITE_MAILBOXES, MIGRATE_FAVORITE_MAILBOXES as any),
    SETUP(),
  ])
}
